import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Box,
  CircularProgress,
  Button,
  Snackbar,
  Alert,
  TextField,
  Card,
  CardContent,
  IconButton,
  Stack,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import TPC_URL from "../../../utils/TPC_URL";
import axios from "axios";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import FactoryIcon from "@mui/icons-material/Factory";
import SaveIcon from "@mui/icons-material/Save";
import PublishIcon from "@mui/icons-material/Publish";
import LockIcon from "@mui/icons-material/Lock";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import dayjs from "dayjs";
import "dayjs/locale/ko";

dayjs.locale("ko");

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100vh - 65px);
  background-color: #f5f5f5;
  overflow: hidden;
  box-sizing: border-box;
`;

const FrameContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1;
  overflow: hidden;
`;

const ToolbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;
`;

const ContentContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const DateSection = styled.div`
  margin-bottom: 32px;
  border: 3px solid #1976d2;
  border-radius: 12px;
  overflow: hidden;
  background-color: white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
`;

const DateHeader = styled.div`
  background-color: #e3f2fd;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const ShiftContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  padding: 20px;
`;

const ShiftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ShiftLabel = styled.div`
  background-color: ${(props) => (props.shift === 1 ? "#e3f2fd" : "#fce4ec")};
  color: ${(props) => (props.shift === 1 ? "#1565c0" : "#c2185b")};
  padding: 12px;
  border-radius: 8px;
  text-align: center;
  font-weight: bold;
  font-size: 1.1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const safeToLocaleString = (value) => {
  if (value === null || value === undefined || value === "") return "0";
  const num = typeof value === "number" ? value : parseInt(value);
  if (isNaN(num)) return "0";
  return num.toLocaleString();
};

function WorkOrder() {
  const { group_code, project_code } = useParams();

  const [loading, setLoading] = useState(false);
  const [groupedByDate, setGroupedByDate] = useState({});
  const [customQuantities, setCustomQuantities] = useState({});
  const [itemInformation, setItemInformation] = useState([]);
  const [allMachines, setAllMachines] = useState([]);
  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
  const [currentGroupForAdd, setCurrentGroupForAdd] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [earliestDateTime, setEarliestDateTime] = useState(null);
  const [baseDate, setBaseDate] = useState(null);

  const [publishDialog, setPublishDialog] = useState({
    open: false,
    group: null,
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ✅ 주간 교대 시간대
  const getDayShiftTimeRange = (date) => {
    const dayOfWeek = dayjs(date).day(); // 0=일요일, 6=토요일
    if (dayOfWeek === 0) return "휴무";
    if (dayOfWeek === 6) return "08:00-12:00";
    return "08:00-12:00, 13:00-17:00";
  };

  // ✅ 야간 교대 시간대
  const getNightShiftTimeRange = (date) => {
    const dayOfWeek = dayjs(date).day();
    const nextDay = dayjs(date).add(1, "day").format("MM/DD");

    if (dayOfWeek === 0) return "휴무";
    if (dayOfWeek === 5) return `18:00-${nextDay} 08:00`;
    if (dayOfWeek === 6) return `00:00-08:00`;
    return `18:00-${nextDay} 07:00`;
  };

  const fetchBaseDate = useCallback(async () => {
    try {
      const response = await axios.get(
        `${TPC_URL}/api/tpc/getBaseDate?group_code=${group_code}&project_code=${project_code}`
      );

      if (response.data && response.data.baseDate) {
        setBaseDate(response.data.baseDate);
      } else {
        setBaseDate(dayjs().format("YYYY-MM-DD"));
      }
    } catch (error) {
      console.error("기준 날짜 조회 실패:", error);
      setBaseDate(dayjs().format("YYYY-MM-DD"));
    }
  }, [group_code, project_code]);

  useEffect(() => {
    fetchBaseDate();
    fetchItemInformation();
    fetchAllMachines();
    fetchRecentResults();
  }, [group_code, project_code, fetchBaseDate]);

  const fetchAllMachines = async () => {
    try {
      const response = await axios.get(
        `${TPC_URL}/api/instruction/getAllMachines?group_code=${group_code}&project_code=${project_code}`
      );
      if (response.data && Array.isArray(response.data)) {
        setAllMachines(response.data);
      }
    } catch (error) {
      console.error("Error fetching all machines:", error);
    }
  };

  const fetchItemInformation = async () => {
    try {
      const response = await axios.get(
        `${TPC_URL}/api/instruction/getItemInformation?group_code=${group_code}&project_code=${project_code}`
      );
      if (response.data && Array.isArray(response.data)) {
        setItemInformation(response.data);
      }
    } catch (error) {
      console.error("Error fetching item information:", error);
    }
  };

  const fetchRecentResults = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${TPC_URL}/api/instruction/getRecentResults?group_code=${group_code}&project_code=${project_code}`
      );
      const typeCount = response.data.reduce((acc, item) => {
        acc[item.type] = (acc[item.type] || 0) + 1;
        return acc;
      }, {});
      if (response.data && Array.isArray(response.data)) {
        const planData = response.data.filter((item) => item.type === 'plan');
        if (planData.length > 0) {
          const sorted = [...planData].sort((a, b) => {
            const dateCompare = dayjs(a.prod_date_only).diff(
              dayjs(b.prod_date_only)
            );
            if (dateCompare !== 0) return dateCompare;
            return a.shift_id - b.shift_id;
          });
          setEarliestDateTime({
            date: sorted[0].prod_date_only,
            shift_id: sorted[0].shift_id,
          });
        }
        const grouped = {};
        planData.forEach((item) => {
          const date = item.prod_date_only;
          const key = `${date}_${item.machine_id}_${item.shift_id}`;
  
          if (!grouped[key]) {
            grouped[key] = {
              id: key,
              date: date,
              machine_id: item.machine_id,
              machine_name: item.machine_name,
              shift_id: item.shift_id,
              shift_name: item.shift_name || `Shift ${item.shift_id}`,
              is_published: item.is_published || 0,
              instruction_id: item.instruction_id || null,
              instruction_code: item.instruction_code || null,
              register_date: item.register_date || null,
              items: [],
            };
          }
          grouped[key].items.push(item);
        });
        Object.values(grouped).forEach((group) => {
          const itemMap = new Map();
          if (group.is_published === 1) {
            group.items.forEach((item) => {
              const key = item.item_code;
              if (itemMap.has(key)) {
              } else {
                itemMap.set(key, {
                  ...item,
                  qty: item.published_qty || item.qty,
                });
              }
            });
          } else {
            group.items.forEach((item) => {
              if (itemMap.has(item.item_code)) {
                const existingItem = itemMap.get(item.item_code);
                existingItem.qty = (existingItem.qty || 0) + (item.qty || 0);
              } else {
                itemMap.set(item.item_code, { ...item });
              }
            });
          }
  
          group.items = Array.from(itemMap.values());
        });
  
        const byDate = {};
        Object.values(grouped).forEach((group) => {
          if (!byDate[group.date]) {
            byDate[group.date] = {
              day: {},
              night: {},
            };
          }
          const shiftKey = group.shift_id === 1 ? "day" : "night";
          if (!byDate[group.date][shiftKey][group.machine_id]) {
            byDate[group.date][shiftKey][group.machine_id] = group;
          }
        });
  
        const sortedByDate = Object.keys(byDate)
          .sort((a, b) => dayjs(a).diff(dayjs(b)))
          .reduce((acc, date) => {
            acc[date] = byDate[date];
            return acc;
          }, {});
  
        setGroupedByDate(sortedByDate);
  
        const initialCustomQty = {};
        Object.values(grouped).forEach((group) => {
          if (Array.isArray(group.items)) {
            group.items.forEach((item) => {
              initialCustomQty[`${group.id}_${item.grid_id}`] = "";
            });
          }
        });
        setCustomQuantities(initialCustomQty);

        loadTempData(sortedByDate);
      }
    } catch (error) {
      console.error("Error fetching recent results:", error);
      setSnackbar({
        open: true,
        message: "데이터를 불러오는 중 오류가 발생했습니다.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };
          
  const loadTempData = (currentGroupedData) => {
    const storageKey = `temp_instruction_${group_code}_${project_code}`;
    const tempData = JSON.parse(localStorage.getItem(storageKey) || "[]");

    if (tempData.length === 0) return;

    const newGroupedByDate = { ...currentGroupedData };
    const newCustomQty = { ...customQuantities };

    tempData.forEach((tempGroup) => {
      const { date, shift_id, machine_id, items } = tempGroup;

      if (!newGroupedByDate[date]) {
        newGroupedByDate[date] = { day: {}, night: {} };
      }

      const shiftKey = shift_id === 1 ? "day" : "night";

      if (newGroupedByDate[date][shiftKey][machine_id]) {
        const existingGroup = newGroupedByDate[date][shiftKey][machine_id];

        if (Array.isArray(items)) {
          items.forEach((tempItem) => {
            const existingItem = Array.isArray(existingGroup.items)
              ? existingGroup.items.find(
                  (item) => item.grid_id === tempItem.grid_id
                )
              : null;

            if (existingItem) {
              newCustomQty[`${existingGroup.id}_${tempItem.grid_id}`] =
                tempItem.custom_qty || "";
            } else {
              if (!Array.isArray(existingGroup.items)) {
                existingGroup.items = [];
              }
              existingGroup.items.push(tempItem);
              newCustomQty[`${existingGroup.id}_${tempItem.grid_id}`] =
                tempItem.custom_qty || "";
            }
          });
        }
      } else {
        const newGroup = {
          id: tempGroup.id,
          date,
          machine_id,
          machine_name: tempGroup.machine_name,
          shift_id,
          shift_name: tempGroup.shift_name,
          is_published: 0,
          instruction_id: null,
          instruction_code: null,
          register_date: null,
          items: Array.isArray(items)
            ? items.map((item) => {
                newCustomQty[`${tempGroup.id}_${item.grid_id}`] =
                  item.custom_qty || "";
                return item;
              })
            : [],
        };

        newGroupedByDate[date][shiftKey][machine_id] = newGroup;
      }
    });

    setGroupedByDate(newGroupedByDate);
    setCustomQuantities(newCustomQty);
  };

  const isDateShiftDisabled = (date, shiftId) => {
    if (!earliestDateTime) return false;

    const currentDate = dayjs(date);
    const earliestDate = dayjs(earliestDateTime.date);

    if (currentDate.isBefore(earliestDate)) return true;
    if (
      currentDate.isSame(earliestDate, "day") &&
      shiftId < earliestDateTime.shift_id
    )
      return true;

    return false;
  };

  const handleCustomQtyChange = (groupId, gridId, value) => {
    const key = `${groupId}_${gridId}`;
    setCustomQuantities((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleOpenAddItem = (group) => {
    setCurrentGroupForAdd(group);
    setSelectedItemId("");
    setAddItemModalOpen(true);
  };

  const handleAddItem = () => {
    if (!selectedItemId || !currentGroupForAdd) return;

    const selectedItem = itemInformation.find(
      (item) =>
        item.item_id === parseInt(selectedItemId) &&
        item.machine_id === currentGroupForAdd.machine_id
    );

    if (!selectedItem) return;

    const currentItems = Array.isArray(currentGroupForAdd.items)
      ? currentGroupForAdd.items
      : [];

    const existingItem = currentItems.find(
      (item) => item.item_id === selectedItem.item_id
    );

    if (existingItem) {
      setSnackbar({
        open: true,
        message: "이미 해당 아이템이 추가되어 있습니다.",
        severity: "warning",
      });
      setAddItemModalOpen(false);
      return;
    }

    const newItem = {
      grid_id: `manual_${Date.now()}`,
      item_id: selectedItem.item_id,
      item_code: selectedItem.item_code,
      item_name: selectedItem.item_name,
      shift_id: currentGroupForAdd.shift_id,
      prod_date: currentGroupForAdd.date,
      prod_date_only: currentGroupForAdd.date,
      qty: 0,
      proc_id: selectedItem.proc_id,
      proc_name: selectedItem.proc_name,
      proc_sequence_no: selectedItem.proc_sequence_no,
      proc_time: selectedItem.proc_time,
      machine_id: selectedItem.machine_id,
      machine_name: selectedItem.machine_name,
    };

    setGroupedByDate((prev) => {
      const updated = { ...prev };
      const date = currentGroupForAdd.date;
      const shiftKey = currentGroupForAdd.shift_id === 1 ? "day" : "night";

      if (!updated[date]) {
        updated[date] = { day: {}, night: {} };
      }
      if (!updated[date][shiftKey]) {
        updated[date][shiftKey] = {};
      }

      if (updated[date][shiftKey][currentGroupForAdd.machine_id]) {
        const existingGroup =
          updated[date][shiftKey][currentGroupForAdd.machine_id];
        const existingItems = Array.isArray(existingGroup.items)
          ? existingGroup.items
          : [];
        updated[date][shiftKey][currentGroupForAdd.machine_id] = {
          ...existingGroup,
          items: [...existingItems, newItem],
        };
      } else {
        updated[date][shiftKey][currentGroupForAdd.machine_id] = {
          ...currentGroupForAdd,
          items: [newItem],
        };
      }

      return updated;
    });

    setCustomQuantities((prev) => ({
      ...prev,
      [`${currentGroupForAdd.id}_${newItem.grid_id}`]: "",
    }));

    setSnackbar({
      open: true,
      message: `${selectedItem.item_code} 아이템이 추가되었습니다.`,
      severity: "success",
    });

    setAddItemModalOpen(false);
  };

  const handleDeleteItem = (groupId, gridId, date, shiftId, machineId) => {
    setGroupedByDate((prev) => {
      const updated = { ...prev };
      const shiftKey = shiftId === 1 ? "day" : "night";

      const group = updated[date][shiftKey][machineId];
      if (group && Array.isArray(group.items)) {
        group.items = group.items.filter((item) => item.grid_id !== gridId);
      }

      return updated;
    });

    setSnackbar({
      open: true,
      message: "항목이 삭제되었습니다.",
      severity: "info",
    });
  };

  const handleTempPublish = (group) => {
    const storageKey = `temp_instruction_${group_code}_${project_code}`;
    const existing = JSON.parse(localStorage.getItem(storageKey) || "[]");

    const items = Array.isArray(group.items) ? group.items : [];

    const instructionData = {
      id: group.id,
      date: group.date,
      shift_id: group.shift_id,
      shift_name: group.shift_name,
      machine_id: group.machine_id,
      machine_name: group.machine_name,
      saved_at: new Date().toISOString(),
      items: items.map((item) => ({
        grid_id: item.grid_id,
        item_id: item.item_id,
        item_code: item.item_code,
        item_name: item.item_name,
        ai_qty: item.qty || 0,
        custom_qty:
          parseInt(customQuantities[`${group.id}_${item.grid_id}`]) || 0,
        production_qty:
          parseInt(customQuantities[`${group.id}_${item.grid_id}`]) ||
          item.qty ||
          0,
      })),
    };

    const filtered = existing.filter((item) => item.id !== group.id);
    filtered.push(instructionData);
    localStorage.setItem(storageKey, JSON.stringify(filtered));

    setSnackbar({
      open: true,
      message: isTempPublished(group)
        ? "임시발행 내용이 수정되었습니다."
        : "임시발행되었습니다.",
      severity: "success",
    });

    setGroupedByDate((prev) => ({ ...prev }));
  };

  const handleOpenPublishDialog = (group) => {
    setPublishDialog({
      open: true,
      group,
    });
  };

  const handleClosePublishDialog = () => {
    setPublishDialog({
      open: false,
      group: null,
    });
  };

  const handleConfirmPublish = async () => {
    const group = publishDialog.group;
    handleClosePublishDialog();
  
    try {
      const items = Array.isArray(group.items) ? group.items : [];
  
      console.log('\n========================================');
      console.log('🚀 PUBLISHING WORK ORDER');
      console.log('========================================');
      console.log('📋 Group Info:', {
        id: group.id,
        date: group.date,
        shift_id: group.shift_id,
        machine_id: group.machine_id,
        machine_name: group.machine_name,
        total_items: items.length
      });
  
      console.log('\n📦 Group Items (RAW):');
      items.forEach((item, idx) => {
        console.log(`  [${idx}]`, {
          item_id: item.item_id,
          item_code: item.item_code,
          grid_id: item.grid_id,
          qty: item.qty
        });
      });
  
      const instructionData = items.map((item, index) => {
        const customKey = `${group.id}_${item.grid_id}`;
        const customValue = customQuantities[customKey];
  
        console.log(`\n  Processing item [${index}]: ${item.item_code}`);
        console.log(`    customKey: ${customKey}`);
        console.log(`    customValue: ${customValue}`);
  
        const hasCustomInput =
          customValue !== "" &&
          customValue !== undefined &&
          customValue !== null;
  
        const customQty = hasCustomInput ? parseInt(customValue, 10) : null;
        const productionQty = hasCustomInput
          ? parseInt(customValue, 10)
          : item.qty || 0;
  
        const result = {
          item_id: item.item_id,
          item_code: item.item_code,
          shift_id: group.shift_id,
          machine_id: group.machine_id,
          ai_qty: item.qty || 0,
          custom_qty: customQty,
          production_qty: productionQty,
          instruction_date: group.date,
        };
  
        console.log(`    Result:`, result);
  
        return result;
      });
  
      console.log('\n✅ Final Instruction Data:');
      console.log('Total items to send:', instructionData.length);
      instructionData.forEach((item, idx) => {
        console.log(`  [${idx}]`, {
          item_id: item.item_id,
          item_code: item.item_code,
          ai_qty: item.ai_qty,
          custom_qty: item.custom_qty,
          production_qty: item.production_qty
        });
      });
  
      const payload = {
        group_code,
        project_code,
        info: instructionData,
      };
  
      console.log('\n📤 Sending to API:');
      console.log(JSON.stringify(payload, null, 2));
  
      const response = await axios.post(
        `${TPC_URL}/api/instruction/postInstruction`,
        payload
      );
  
      console.log('\n✅ API Response:');
      console.log(response.data);
      console.log('========================================\n');
  
      setSnackbar({
        open: true,
        message: "작업지시가 발행되었습니다.",
        severity: "success",
      });
  
      const storageKey = `temp_instruction_${group_code}_${project_code}`;
      const existing = JSON.parse(localStorage.getItem(storageKey) || "[]");
      const filtered = existing.filter((g) => g.id !== group.id);
      localStorage.setItem(storageKey, JSON.stringify(filtered));
  
      await fetchRecentResults();
    } catch (error) {
      console.error('\n❌ Publishing Error:');
      console.error('Error details:', error);
      console.error('Response:', error.response?.data);
      console.log('========================================\n');
      
      setSnackbar({
        open: true,
        message: "작업지시 발행 중 오류가 발생했습니다.",
        severity: "error",
      });
    }
  };
  
  const getTotalAiQty = (group) => {
    if (!group || !Array.isArray(group.items)) return 0;
    
    const total = group.items.reduce((sum, item) => {
      return sum + (item.qty || 0);
    }, 0);
    
    // ✅ 디버깅
    if (group.id === '2026-06-02_63_1') {
      console.log('📊 getTotalAiQty:', {
        group_id: group.id,
        items_count: group.items.length,
        items: group.items.map(item => ({ code: item.item_code, qty: item.qty })),
        total
      });
    }
    
    return total;
  };
  
  const getTotalCustomQty = (group) => {
    if (!group || !Array.isArray(group.items)) return 0;
    
    const total = group.items.reduce((sum, item) => {
      const customQty = customQuantities[`${group.id}_${item.grid_id}`] || "";
      const value = parseInt(customQty) || 0;
      return sum + value;
    }, 0);
    
    // ✅ 디버깅
    if (group.id === '2026-06-02_63_1') {
      console.log('📊 getTotalCustomQty:', {
        group_id: group.id,
        items: group.items.map(item => ({
          code: item.item_code,
          grid_id: item.grid_id,
          customValue: customQuantities[`${group.id}_${item.grid_id}`],
          parsed: parseInt(customQuantities[`${group.id}_${item.grid_id}`]) || 0
        })),
        total
      });
    }
    
    return total;
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getAvailableItemsForGroup = (group) => {
    if (!group) return [];
    return itemInformation.filter(
      (item) => item.machine_id === group.machine_id
    );
  };

  const isTempPublished = (group) => {
    const storageKey = `temp_instruction_${group_code}_${project_code}`;
    const tempData = JSON.parse(localStorage.getItem(storageKey) || "[]");
    return tempData.some((g) => g.id === group.id);
  };

  const getTempPublishedData = (group) => {
    const storageKey = `temp_instruction_${group_code}_${project_code}`;
    const tempData = JSON.parse(localStorage.getItem(storageKey) || "[]");
    return tempData.find((g) => g.id === group.id);
  };

  const isGroupPublished = (group) => {
    if (!group) return false;
    return group.is_published === 1 || !!group.instruction_id;
  };

  const renderMachineCard = (date, shiftId, machine, shiftKey) => {
    const groups = groupedByDate[date]?.[shiftKey] || {};
    const group = groups[machine.machine_id];

    const isDisabled = isDateShiftDisabled(date, shiftId);
    const isPublished = group ? isGroupPublished(group) : false;
    const tempPublished = group ? isTempPublished(group) : false;
    const tempData = group ? getTempPublishedData(group) : null;

    if (!group) {
      const emptyGroupId = `${date}_${machine.machine_id}_${shiftId}`;
      const emptyGroup = {
        id: emptyGroupId,
        date,
        machine_id: machine.machine_id,
        machine_name: machine.machine_name,
        shift_id: shiftId,
        shift_name: shiftId === 1 ? "주간" : "야간",
        items: [],
        is_published: 0,
      };

      return (
        <Card
          key={emptyGroupId}
          sx={{
            boxShadow: 2,
            borderRadius: 2,
            border: isDisabled ? "2px solid #bbb" : "2px dashed #ccc",
            opacity: isDisabled ? 0.5 : 1,
            bgcolor: isDisabled ? "#f5f5f5" : "white",
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1.5,
                pb: 1.5,
                borderBottom: "2px solid #e0e0e0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FactoryIcon sx={{ color: "#666" }} />
                <Typography variant="h6" fontWeight={600}>
                  {machine.machine_name}
                </Typography>
                {isDisabled && (
                  <Chip
                    icon={<LockIcon />}
                    label="추가 불가"
                    size="small"
                    color="default"
                  />
                )}
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenAddItem(emptyGroup)}
                  size="small"
                  disabled={isDisabled}
                >
                  추가
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={() => handleTempPublish(emptyGroup)}
                  size="small"
                  color="secondary"
                  disabled={isDisabled || emptyGroup.items.length === 0}
                >
                  임시발행
                </Button>
              </Box>
            </Box>

            <Box
              sx={{
                minHeight: 80,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#999",
                border: "1px dashed #ddd",
                borderRadius: 1,
                bgcolor: "#fafafa",
              }}
            >
              <Typography variant="body2">
                {isDisabled ? "추가 불가 영역" : "아이템 없음"}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }
    if (date === '2026-06-02' && shiftId === 1 && machine.machine_id === 63) {
      console.log('\n🎴 Rendering Card: 2026-06-02 주간 절단기');
      console.log('Group items:', group.items.length);
      group.items.forEach((item, idx) => {
        console.log(`  [${idx}]`, {
          code: item.item_code,
          qty: item.qty,
          grid_id: item.grid_id
        });
      });
      console.log('Total AI Qty:', getTotalAiQty(group));
      console.log('Total Custom Qty:', getTotalCustomQty(group));
    }
  

    return (
      <Card
        key={group.id}
        sx={{
          boxShadow: 3,
          borderRadius: 2,
          border: isPublished
            ? "3px solid #9e9e9e"
            : tempPublished
            ? "3px solid #4caf50"
            : shiftId === 1
            ? "2px solid #1976d2"
            : "2px solid #c2185b",
          opacity: isDisabled || isPublished ? 0.7 : 1,
          bgcolor: isPublished ? "#f5f5f5" : "white",
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
              pb: 1.5,
              borderBottom: "2px solid #e0e0e0",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}
            >
              <FactoryIcon sx={{ color: "#666" }} />
              <Typography variant="h6" fontWeight={600}>
                {group.machine_name}
              </Typography>

              {isPublished && (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="발행됨"
                  size="small"
                  color="default"
                  sx={{ fontWeight: "bold" }}
                />
              )}

              {!isPublished && tempPublished && tempData && (
                <Chip
                  label={`임시발행됨 (${dayjs(tempData.saved_at).format(
                    "HH:mm"
                  )})`}
                  size="small"
                  color="success"
                  sx={{ fontWeight: "bold" }}
                />
              )}

              {isDisabled && !isPublished && (
                <Chip
                  icon={<LockIcon />}
                  label="추가 불가"
                  size="small"
                  color="default"
                />
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {!isPublished && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenAddItem(group)}
                    size="small"
                    disabled={isDisabled || isPublished}
                  >
                    추가
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={() => handleTempPublish(group)}
                    size="small"
                    color="secondary"
                    disabled={isDisabled || isPublished}
                  >
                    {tempPublished ? "재임시발행" : "임시발행"}
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={<PublishIcon />}
                    onClick={() => handleOpenPublishDialog(group)}
                    size="small"
                    color="primary"
                    disabled={isDisabled}
                  >
                    발행
                  </Button>
                </>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 1,
              mb: 1.5,
              p: 1,
              bgcolor: "#f9f9f9",
              borderRadius: 1,
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                품목 수
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {Array.isArray(group.items) ? group.items.length : 0}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                계획수량
              </Typography>
              <Typography variant="body2" color="primary" fontWeight="bold">
                {safeToLocaleString(getTotalAiQty(group))}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                지시 수량
              </Typography>
              <Typography variant="body2" color="secondary" fontWeight="bold">
                {safeToLocaleString(getTotalCustomQty(group))}
              </Typography>
            </Box>
          </Box>

          <Stack spacing={1.5}>
            {Array.isArray(group.items) &&
              group.items.map((item, index) => (
                <Paper
                  key={item.grid_id}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    bgcolor: index % 2 === 0 ? "#fafafa" : "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      {item.item_code}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 80,
                          textAlign: "center",
                          p: 0.5,
                          bgcolor: "white",
                          borderRadius: 1,
                          border: "1px solid #e3f2fd",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          계획
                        </Typography>
                        <Typography
                          variant="body2"
                          color="primary"
                          fontWeight="bold"
                        >
                          {safeToLocaleString(item.qty)}
                        </Typography>
                      </Box>

                      <TextField
                        placeholder="설정"
                        type="number"
                        size="small"
                        value={
                          customQuantities[`${group.id}_${item.grid_id}`] || ""
                        }
                        onChange={(e) =>
                          handleCustomQtyChange(
                            group.id,
                            item.grid_id,
                            e.target.value
                          )
                        }
                        sx={{ width: 100 }}
                        InputProps={{
                          inputProps: { min: 0 },
                        }}
                        disabled={isDisabled || isPublished}
                      />

                      <IconButton
                        color="error"
                        onClick={() =>
                          handleDeleteItem(
                            group.id,
                            item.grid_id,
                            date,
                            group.shift_id,
                            group.machine_id
                          )
                        }
                        size="small"
                        disabled={isDisabled || isPublished}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              ))}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Wrapper>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <CircularProgress size={60} />
        </Box>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <FrameContainer>
        <ToolbarContainer>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ fontSize: "22px !important", color: "#111" }}
          >
            설비별 작업지시서 등록
          </Typography>

          {/* ✅ 기준 날짜 전체 표시 */}
          {baseDate && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                기준 날짜:
              </Typography>
              <Chip
                label={dayjs(baseDate).format("YYYY-MM-DD (ddd)")}
                size="small"
                color="primary"
                sx={{ fontWeight: "bold" }}
              />
            </Box>
          )}
        </ToolbarContainer>

        <ContentContainer>
          {Object.keys(groupedByDate).length === 0 ? (
            <Paper sx={{ p: 4, textAlign: "center", bgcolor: "white" }}>
              <Typography variant="h6" color="text.secondary">
                표시할 데이터가 없습니다.
              </Typography>
            </Paper>
          ) : (
            Object.entries(groupedByDate).map(([date, shifts]) => (
              <DateSection key={date}>
                {/* ✅ 개선된 DateHeader */}
                <DateHeader>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      flex: 1,
                    }}
                  >
                    <Typography variant="h6" fontWeight={600} color="#1565c0">
                      📅 {dayjs(date).format("YYYY년 MM월 DD일")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dayjs(date).format("dddd")}
                    </Typography>

                    {baseDate && date === baseDate && (
                      <Chip
                        label="📌 기준 날짜"
                        size="small"
                        color="primary"
                        sx={{
                          fontWeight: "bold",
                          fontSize: "0.85rem",
                          boxShadow: "0 2px 4px rgba(25, 118, 210, 0.3)",
                        }}
                      />
                    )}
                  </Box>

                  {/* ✅ 실제 작업 시간대 안내 */}
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Chip
                      label={`주간: ${getDayShiftTimeRange(date)}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: "#1976d2",
                        color: "#1565c0",
                        fontWeight: "bold",
                      }}
                    />
                    <Chip
                      label={`야간: ${getNightShiftTimeRange(date)}`}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: "#c2185b",
                        color: "#c2185b",
                        fontWeight: "bold",
                      }}
                    />
                  </Box>
                </DateHeader>

                <ShiftContainer>
                  {/* ✅ 주간 컬럼 */}
                  <ShiftColumn>
                    <Box sx={{ position: "relative" }}>
                      <ShiftLabel shift={1}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <span>🌞 주간</span>
                          <Typography
                            variant="caption"
                            sx={{ opacity: 0.8, fontSize: "0.85rem" }}
                          >
                            ({getDayShiftTimeRange(date)})
                          </Typography>
                        </Box>
                      </ShiftLabel>
                    </Box>

                    {allMachines.map((machine) =>
                      renderMachineCard(date, 1, machine, "day")
                    )}
                  </ShiftColumn>

                  {/* ✅ 야간 컬럼 */}
                  <ShiftColumn>
                    <Box sx={{ position: "relative" }}>
                      <ShiftLabel shift={2}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <span>🌙 야간</span>
                          <Typography
                            variant="caption"
                            sx={{ opacity: 0.8, fontSize: "0.85rem" }}
                          >
                            ({getNightShiftTimeRange(date)})
                          </Typography>

                          {/* ✅ 야간 교대 설명 툴팁 */}
                          <Tooltip
                            title={
                              <Box>
                                <Typography
                                  variant="caption"
                                  display="block"
                                  fontWeight="bold"
                                >
                                  야간 교대 시간 안내
                                </Typography>
                                <Typography
                                  variant="caption"
                                  display="block"
                                  sx={{ mt: 0.5 }}
                                >
                                  • 평일: 전날 18:00 ~ 익일 07:00
                                </Typography>
                                <Typography variant="caption" display="block">
                                  • 금요일: 18:00 ~ 토요일 08:00
                                </Typography>
                                <Typography
                                  variant="caption"
                                  display="block"
                                  sx={{ mt: 0.5, fontWeight: "bold" }}
                                >
                                  ⚠️ 새벽 시간대는 전날 야간 교대에 포함됩니다
                                </Typography>
                              </Box>
                            }
                            arrow
                          >
                            <InfoIcon
                              sx={{
                                fontSize: 16,
                                opacity: 0.7,
                                cursor: "help",
                              }}
                            />
                          </Tooltip>
                        </Box>
                      </ShiftLabel>
                    </Box>

                    {allMachines.map((machine) =>
                      renderMachineCard(date, 2, machine, "night")
                    )}
                  </ShiftColumn>
                </ShiftContainer>
              </DateSection>
            ))
          )}
        </ContentContainer>
      </FrameContainer>

      {/* ✅ 발행 확인 Dialog */}
      <Dialog
        open={publishDialog.open}
        onClose={handleClosePublishDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {baseDate && publishDialog.group?.date !== baseDate ? (
            <>
              <WarningIcon color="warning" />
              기준 날짜가 아닌 작업지시 발행
            </>
          ) : (
            <>
              <PublishIcon color="primary" />
              작업지시 발행 확인
            </>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {/* ✅ 기준 날짜 경고 */}
          {baseDate && publishDialog.group?.date !== baseDate && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                ⚠️ 기준 날짜가 아닙니다!
              </Typography>
              <Typography variant="caption">
                기준 날짜: {dayjs(baseDate).format("YYYY년 MM월 DD일")}
                <br />
                발행 날짜:{" "}
                {dayjs(publishDialog.group?.date).format("YYYY년 MM월 DD일")}
              </Typography>
            </Alert>
          )}

          {/* ✅ 발행 정보 요약 */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="bold" gutterBottom>
              <FactoryIcon
                sx={{ fontSize: 18, verticalAlign: "middle", mr: 0.5 }}
              />
              {publishDialog.group?.machine_name}
            </Typography>
            <Box
              sx={{ display: "flex", gap: 2, ml: 3, flexWrap: "wrap" }}
            >
              <Chip
                label={dayjs(publishDialog.group?.date).format(
                  "YYYY-MM-DD (ddd)"
                )}
                size="small"
                color="primary"
                variant="outlined"
              />
              <Chip
                label={`${publishDialog.group?.shift_name} (${
                  publishDialog.group?.shift_id === 1
                    ? getDayShiftTimeRange(publishDialog.group?.date)
                    : getNightShiftTimeRange(publishDialog.group?.date)
                })`}
                size="small"
                color={
                  publishDialog.group?.shift_id === 1 ? "primary" : "secondary"
                }
                variant="outlined"
              />
              <Chip
                label={`품목 ${publishDialog.group?.items?.length || 0}개`}
                size="small"
                color="default"
                variant="outlined"
              />
            </Box>
          </Box>

          {/* ✅ 품목별 생산 계획 테이블 */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              📦 생산 계획 상세
            </Typography>
            <Paper variant="outlined" sx={{ mt: 1 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 1fr 1fr",
                  gap: 1,
                  p: 1.5,
                  bgcolor: "#f5f5f5",
                  borderBottom: "2px solid #e0e0e0",
                  fontWeight: "bold",
                }}
              >
                <Typography variant="caption" fontWeight="bold">
                  품목코드
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  textAlign="center"
                >
                  계획 수량
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  textAlign="center"
                >
                  설정 수량
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  textAlign="center"
                >
                  발행 수량
                </Typography>
              </Box>

              {/* ✅ 품목 리스트 */}
              {publishDialog.group?.items?.map((item, index) => {
                const customKey = `${publishDialog.group.id}_${item.grid_id}`;
                const customValue = customQuantities[customKey];

                const hasCustomInput =
                  customValue !== "" &&
                  customValue !== undefined &&
                  customValue !== null;

                const customQty = hasCustomInput
                  ? parseInt(customValue, 10)
                  : null;
                const productionQty = hasCustomInput
                  ? parseInt(customValue, 10)
                  : item.qty || 0;

                return (
                  <Box
                    key={item.grid_id}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1fr 1fr 1fr",
                      gap: 1,
                      p: 1.5,
                      bgcolor: index % 2 === 0 ? "white" : "#fafafa",
                      borderBottom:
                        index < publishDialog.group.items.length - 1
                          ? "1px solid #e0e0e0"
                          : "none",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {item.item_code}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.item_name}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      textAlign="center"
                      color="text.secondary"
                    >
                      {safeToLocaleString(item.qty)}
                    </Typography>
                    <Typography
                      variant="body2"
                      textAlign="center"
                      color={hasCustomInput ? "primary" : "text.disabled"}
                      fontWeight={hasCustomInput ? "bold" : "normal"}
                    >
                      {hasCustomInput ? safeToLocaleString(customQty) : "-"}
                    </Typography>
                    <Typography
                      variant="body2"
                      textAlign="center"
                      color="secondary"
                      fontWeight="bold"
                    >
                      {safeToLocaleString(productionQty)}
                    </Typography>
                  </Box>
                );
              })}
            </Paper>
          </Box>

          {/* ✅ 총계 */}
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: "#f5f5f5",
              borderRadius: 1,
              display: "flex",
              justifyContent: "space-around",
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="caption" color="text.secondary">
                총 계획 수량
              </Typography>
              <Typography variant="h6" color="primary" fontWeight="bold">
                {safeToLocaleString(getTotalAiQty(publishDialog.group))}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="caption" color="text.secondary">
                총 지시 수량
              </Typography>
              <Typography variant="h6" color="secondary" fontWeight="bold">
                {safeToLocaleString(getTotalCustomQty(publishDialog.group))}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClosePublishDialog} color="inherit">
            취소
          </Button>
          <Button
            onClick={handleConfirmPublish}
            variant="contained"
            color="primary"
            startIcon={<PublishIcon />}
          >
            발행 확정
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ 아이템 추가 Modal */}
      <Dialog
        open={addItemModalOpen}
        onClose={() => setAddItemModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>품목 추가</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {currentGroupForAdd?.machine_name} 설비에서 생산 가능한 품목을
              선택하세요.
            </Typography>
            <TextField
              select
              fullWidth
              label="품목 선택"
              value={selectedItemId}
              onChange={(e) => setSelectedItemId(e.target.value)}
              sx={{ mt: 2 }}
              SelectProps={{
                native: true,
              }}
            >
              <option value="">선택하세요</option>
              {getAvailableItemsForGroup(currentGroupForAdd).map((item) => (
                <option key={item.item_id} value={item.item_id}>
                  {item.item_code} - {item.item_name}
                </option>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddItemModalOpen(false)} color="inherit">
            취소
          </Button>
          <Button
            onClick={handleAddItem}
            variant="contained"
            color="primary"
            disabled={!selectedItemId}
          >
            추가
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Wrapper>
  );
}

export default WorkOrder;
